import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, VerificationRequest } from '../types';
import supabase from '../lib/supabase';
import { deleteUserAccount as deleteAccountAPI } from '../lib/auth';
import { setupNotificationsImmediately } from '../utils/mobileNotifications';

interface AuthState {
  user: User | null;
  loading: boolean;
  verificationRequest: VerificationRequest | null;
  onboardingStep: string | null;
  isNewUser: boolean;
  isOAuth: boolean;
  setUser: (user: User | null, isNewSession?: boolean) => void;
  clearUser: () => void;
  setOnboardingStep: (step: string | null) => void;
  setIsNewUser: (isNew: boolean) => void;
  setIsOAuth: (isOAuth: boolean) => void;
  signOut: () => Promise<void>;
  fetchVerificationRequest: () => Promise<void>;
  submitVerificationRequest: (
    credentials: Record<string, any>
  ) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  approveVerification: (userId: string) => Promise<void>;
  subscribeToProfile: () => () => void;
  checkAuthMethod: () => Promise<boolean>;
  deleteAccount: (confirmText: string) => Promise<void>;
  // NEW: Methods for OneSignal management
  syncOneSignalPlayerId: () => Promise<void>;
  clearOneSignalPlayerId: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      loading: true,
      verificationRequest: null,
      onboardingStep: null,
      isNewUser: false,
      isOAuth: false,

      setUser: (user, isNewSession = false) => {
        if (user) {
          // Normalize user data with default values
          const normalizedUser = {
            ...user,
            full_name: user.full_name || '',
            display_name: user.display_name || '',
            role: user.role || 'user',
            verified: user.verified || false,
            spotify_connected: user.spotify_connected || false,
            professions: user.professions || [],
            social: user.social || {},
            onboarding_completed: user.onboarding_completed || false,
            onesignal_player_id: user.onesignal_player_id || null,
          };

          // ✅ CRITICAL: Update state IMMEDIATELY (synchronously)
          set({
            user: normalizedUser,
            loading: false,
          });

          // ✅ CRITICAL: Update localStorage IMMEDIATELY (synchronously)
          localStorage.setItem('authUser', JSON.stringify(normalizedUser));

          // 📱 Setup notifications AFTER state is updated (non-blocking)
          if (isNewSession && normalizedUser.id && !normalizedUser.onesignal_player_id) {
            console.log('🚀 New user session - setting up notifications...');
            
            // ✅ CRITICAL: Don't await - run in background
            setupNotificationsImmediately(normalizedUser.id).then(
              (success) => {
                if (success) {
                  console.log('✅ Notifications setup completed!');
                } else {
                  console.log('⚠️ Notifications setup failed - will retry later');
                }
              }
            ).catch((error) => {
              console.error('❌ Error during notification setup:', error);
            });
          } else if (isNewSession) {
            console.log('📱 Session started - syncing OneSignal player ID...');
            // For returning users, sync their player ID
            get().syncOneSignalPlayerId();
          }
        } else {
          set({ user: null, loading: false });
          localStorage.removeItem('authUser');
        }
      },

      clearUser: () => {
        set({ user: null, loading: false });
        localStorage.removeItem('authUser');
      },

      setOnboardingStep: (step) => {
        set({ onboardingStep: step });
        if (step) {
          localStorage.setItem('onboardingStep', step);
        } else {
          localStorage.removeItem('onboardingStep');
        }
      },

      setIsNewUser: (isNew) => {
        set({ isNewUser: isNew });
      },

      setIsOAuth: (isOAuth) => {
        console.log('Setting isOAuth:', isOAuth);
        set({ isOAuth });
      },

      checkAuthMethod: async () => {
        try {
          const { data } = await supabase.auth.getSession();
          const session = data.session;

          if (!session) {
            console.log('No session found, not OAuth');
            set({ isOAuth: false });
            return false;
          }

          // Check for OAuth provider in app_metadata
          const isOAuth = !!(
            session.user?.app_metadata?.provider &&
            session.user.app_metadata.provider !== 'email'
          );

          console.log('checkAuthMethod - Session:', session);
          console.log(
            'checkAuthMethod - Provider:',
            session.user?.app_metadata?.provider
          );
          console.log('checkAuthMethod - Is OAuth:', isOAuth);

          set({ isOAuth });
          return isOAuth;
        } catch (error) {
          console.error('Error checking auth method:', error);
          set({ isOAuth: false });
          return false;
        }
      },

      signOut: async () => {
        try {
          // NEW: Clear OneSignal player ID before signing out
          const currentUser = get().user;
          if (currentUser?.onesignal_player_id) {
            console.log('🧹 Clearing OneSignal player ID before sign out...');
            try {
              await get().clearOneSignalPlayerId();
            } catch (error) {
              console.error('Failed to clear OneSignal player ID:', error);
              // Continue with sign out even if this fails
            }
          }

          const { error } = await supabase.auth.signOut();
          if (error) throw error;

          set({
            user: null,
            verificationRequest: null,
            onboardingStep: null,
            loading: false,
            isOAuth: false,
          });

          // Clear all auth-related storage
          localStorage.removeItem('authUser');
          localStorage.removeItem('onboardingStep');
          localStorage.removeItem('supabase.auth.token');
          sessionStorage.removeItem('onboardingStep');
          sessionStorage.removeItem('authRedirect');
        } catch (error: any) {
          console.error('Error signing out:', error);
          throw error;
        }
      },

      deleteAccount: async (confirmText: string) => {
        try {
          await deleteAccountAPI(confirmText);
          
          // Clear all state
          set({
            user: null,
            isAuthenticated: false,
            verificationRequest: null,
            onboardingStep: null,
            isOAuth: false
          });
          
          // Clear any cached data
          localStorage.removeItem('onboardingStatus');
          localStorage.removeItem('onboardingStep');
          sessionStorage.clear();
          
        } catch (error) {
          console.error('Account deletion failed:', error);
          throw error;
        }
      },

      // NEW: Sync OneSignal Player ID
      syncOneSignalPlayerId: async () => {
        const { user } = get();
        if (!user?.id || !window.NativelyNotifications) {
          console.log('📱 Cannot sync - no user or not in mobile app');
          return;
        }

        console.log('🔄 Syncing OneSignal Player ID...');

        try {
          const notifications = new window.NativelyNotifications();

          // Check permission status first
          notifications.getPermissionStatus((permResp: any) => {
            console.log('📱 Permission status:', permResp);

            if (permResp?.status === true) {
              // We have permission, get the current player ID
              notifications.getOneSignalId((idResp: any) => {
                console.log('🆔 Current Player ID response:', idResp);

                if (idResp?.playerId && idResp.playerId.trim() !== '') {
                  const currentPlayerId = idResp.playerId.trim();

                  // ALWAYS update if it's different or if we don't have one stored
                  if (currentPlayerId !== user.onesignal_player_id || !user.onesignal_player_id) {
                    console.log('✅ Updating Player ID:', {
                      old: user.onesignal_player_id || 'none',
                      new: currentPlayerId
                    });

                    get().updateProfile({
                      onesignal_player_id: currentPlayerId
                    }).then(() => {
                      console.log('✅ Player ID updated successfully');
                    }).catch((error) => {
                      console.error('❌ Failed to update Player ID:', error);
                    });
                  } else {
                    console.log('✅ Player ID is current');
                  }
                } else {
                  console.log('⚠️ No valid Player ID received');

                  // If we had a player ID before but don't now, clear it
                  if (user.onesignal_player_id) {
                    console.log('🧹 Clearing invalid Player ID');
                    get().updateProfile({
                      onesignal_player_id: null
                    }).catch((error) => {
                      console.error('Failed to clear player ID:', error);
                    });
                  }
                }
              });
            } else {
              console.log('❌ No notification permission');

              // Clear player ID if we don't have permission
              if (user.onesignal_player_id) {
                console.log('🧹 Clearing Player ID due to no permission');
                get().updateProfile({
                  onesignal_player_id: null
                }).catch((error) => {
                  console.error('Failed to clear player ID:', error);
                });
              }
            }
          });
        } catch (error) {
          console.error('❌ Error syncing Player ID:', error);
        }
      },

      // NEW: Clear OneSignal Player ID
      clearOneSignalPlayerId: async () => {
        const { user } = get();
        if (!user) return;

        try {
          await get().updateProfile({
            onesignal_player_id: null
          });
          console.log('✅ OneSignal player ID cleared');
        } catch (error) {
          console.error('❌ Failed to clear OneSignal player ID:', error);
          throw error;
        }
      },

      fetchVerificationRequest: async () => {
        const { user } = get();
        if (!user) return;

        try {
          const { data, error } = await supabase
            .from('verification_requests')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (error) {
            console.error('Error fetching verification request:', error);
            return;
          }

          set({ verificationRequest: data });

          // Update user verified status if request is approved
          if (data?.status === 'approved' && !user.verified) {
            await get().updateProfile({ verified: true });
          }
        } catch (error) {
          console.error('Failed to fetch verification request:', error);
        }
      },

      submitVerificationRequest: async (credentials) => {
        const { user } = get();
        if (!user) throw new Error('User must be logged in');

        try {
          const { data: profileId } = await supabase.rpc(
            'get_current_user_profile_id'
          );

          const { error: insertError } = await supabase
            .from('verification_requests')
            .insert([
              {
                user_id: profileId,
                credentials,
                status: 'pending',
                submission_date: new Date().toISOString(),
              },
            ]);

          if (insertError) throw insertError;

          // Fetch the newly created verification request
          const { data: newRequest, error: fetchError } = await supabase
            .from('verification_requests')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (fetchError) throw fetchError;

          set({ verificationRequest: newRequest });
        } catch (error) {
          console.error('Failed to submit verification request:', error);
          throw error;
        }
      },

      updateProfile: async (data: Partial<User>) => {
        const { user } = get();
        if (!user) throw new Error('No user logged in');

        try {
          // First, get the current profile data AND the profile ID
          const { data: currentProfileData, error: fetchError } =
            await supabase.rpc('get_current_user_profile');

          if (
            fetchError ||
            !currentProfileData ||
            currentProfileData.length === 0
          ) {
            throw new Error('User profile not found. Please contact support.');
          }

          const currentProfile = currentProfileData[0];
          const profileId = currentProfile.id; // Get the profile ID

          // Handle avatar updates for OAuth users
          // Only preserve existing OAuth avatar if no new avatar is provided
          if (
            get().isOAuth &&
            currentProfile.avatar_url &&
            data.avatar_url === undefined
          ) {
            // No new avatar provided, keep existing OAuth avatar
            // Don't delete from data since it's undefined anyway
          }
          // If data.avatar_url has a value (including null for deletion), allow the update

          // Merge with existing data
          const updateData = {
            ...data,
            professions: Array.isArray(data.professions)
              ? data.professions
              : data.professions
              ? [data.professions]
              : currentProfile.professions || [],
            social: {
              ...(currentProfile.social || {}),
              ...(data.social || {}),
            },
            updated_at: new Date().toISOString(),
          };

          // Now use the profile ID in the WHERE clause
          const { data: updatedProfile, error: updateError } = await supabase
            .from('user_profiles')
            .update(updateData)
            .eq('id', profileId) // Use the profile ID from RPC
            .select()
            .single();

          if (updateError) throw updateError;

          if (!updatedProfile) {
            throw new Error('No profile data returned after update');
          }

          // Merge the updated profile with existing user data
          const mergedUser = {
            ...user,
            ...updatedProfile,
            social: {
              ...(user.social || {}),
              ...(updatedProfile.social || {}),
            },
          };

          // ✅ CRITICAL: Update state synchronously
          set({ user: mergedUser });
          localStorage.setItem('authUser', JSON.stringify(mergedUser));

        } catch (error: any) {
          console.error('Profile update failed:', error);
          throw new Error(error.message || 'Failed to update profile');
        }
      },

      approveVerification: async (userId: string) => {
        try {
          // Start a transaction to update both tables
          const { data: profileId } = await supabase.rpc(
            'get_current_user_profile_id'
          );
          const { data: verificationData, error: verificationError } =
            await supabase
              .from('verification_requests')
              .update({
                status: 'approved',
                review_date: new Date().toISOString(),
              })
              .eq('user_id', profileId)
              .select()
              .single();

          if (verificationError) throw verificationError;

          // Update user profile
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .update({
              verified: true,
              updated_at: new Date().toISOString(),
            })
            .select()
            .single();

          if (profileError) throw profileError;

          return profileData;
        } catch (error) {
          console.error('Failed to approve verification:', error);
          throw error;
        }
      },

      subscribeToProfile: () => {
        const { user } = get();
        if (!user) return () => {};

        // Setup subscriptions asynchronously
        const setupSubscriptions = async () => {
          try {
            const { data: profileId } = await supabase.rpc(
              'get_current_user_profile_id'
            );
            if (!profileId) return null;

            // Subscribe to user_profiles changes
            const profileSubscription = supabase
              .channel('profile-changes')
              .on(
                'postgres_changes',
                {
                  event: '*',
                  schema: 'public',
                  table: 'user_profiles',
                  filter: `id=eq.${profileId}`,
                },
                async (payload) => {
                  const updatedProfile = payload.new;

                  // Merge with existing user data to preserve fields
                  const currentUser = get().user;
                  if (!currentUser) return;

                  const updatedUser = {
                    ...currentUser,
                    ...updatedProfile,
                    social: {
                      ...(currentUser.social || {}),
                      ...(updatedProfile.social || {}),
                    },
                  };

                  // ✅ CRITICAL: Update state synchronously
                  set({ user: updatedUser });
                  localStorage.setItem('authUser', JSON.stringify(updatedUser));
                }
              )
              .subscribe();

            // Subscribe to verification_requests changes
            const verificationSubscription = supabase
              .channel('verification-changes')
              .on(
                'postgres_changes',
                {
                  event: '*',
                  schema: 'public',
                  table: 'verification_requests',
                  filter: `user_id=eq.${profileId}`, // Use profileId instead of user.id
                },
                async (payload) => {
                  if (payload.new) {
                    // Update verification request state
                    set({ verificationRequest: payload.new });

                    // If verification was approved, update user profile
                    if (payload.new.status === 'approved') {
                      const { data: profileData } = await supabase.rpc(
                        'get_current_user_profile'
                      );

                      if (profileData && profileData.length > 0) {
                        const currentUser = get().user;
                        if (!currentUser) return;

                        const updatedUser = {
                          ...currentUser,
                          ...profileData[0],
                          social: {
                            ...(currentUser.social || {}),
                            ...(profileData[0].social || {}),
                          },
                        };
                        
                        // ✅ CRITICAL: Update state synchronously
                        set({ user: updatedUser });
                        localStorage.setItem('authUser', JSON.stringify(updatedUser));
                      }
                    }
                  }
                }
              )
              .subscribe();

            return { profileSubscription, verificationSubscription };
          } catch (error) {
            console.error('Error setting up subscriptions:', error);
            return null;
          }
        };

        // Handle visibility change events
        const handleVisibilityChange = () => {
          if (document.visibilityState === 'visible') {
            // NEW: Sync OneSignal player ID when app becomes visible
            console.log('📱 App became visible - syncing OneSignal player ID...');
            get().syncOneSignalPlayerId();
            
            // Refresh user data when tab becomes visible
            const refreshUserData = async () => {
              try {
                const { data: profileData } = await supabase.rpc(
                  'get_current_user_profile'
                );

                if (profileData && profileData.length > 0) {
                  const currentUser = get().user;
                  if (!currentUser) return;

                  const updatedUser = {
                    ...currentUser,
                    ...profileData[0],
                  };
                  
                  // ✅ CRITICAL: Update state synchronously
                  set({ user: updatedUser });
                  localStorage.setItem('authUser', JSON.stringify(updatedUser));
                }

                // Also check auth method
                get().checkAuthMethod();
              } catch (error) {
                // Silently handle errors during refresh
                console.warn('Error refreshing user data:', error);
              }
            };

            refreshUserData();
          }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Store subscriptions reference for cleanup
        let subscriptions = null;
        setupSubscriptions().then((subs) => {
          subscriptions = subs;
        });

        // Return cleanup function
        return () => {
          if (subscriptions) {
            subscriptions.profileSubscription?.unsubscribe();
            subscriptions.verificationSubscription?.unsubscribe();
          }
          document.removeEventListener(
            'visibilitychange',
            handleVisibilityChange
          );
        };
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        onboardingStep: state.onboardingStep,
        isNewUser: state.isNewUser,
        isOAuth: state.isOAuth,
      }),
    }
  )
);

export async function handleBannedUser(userId: string) {
  const { data: bannedUser, error } = await supabase
    .from('banned_users')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();

  console.log('👮 Banned user check:', bannedUser, error);

  if (bannedUser) {
    await useAuthStore.getState().signOut();
    useAuthStore.getState().setUser(null);
    alert('🚫 Your account has been banned.');
    window.location.href = '/'; // Optional: redirect
    return true;
  }

  return false;
}

// Auth state change handler - NO notifications here
supabase.auth.onAuthStateChange(async (event, session) => {
  console.log('🔔 Auth state change:', event, !!session?.user);
  
  // Only handle actual SIGN_IN events for AuthCallback (not INITIAL_SESSION)
  if (event === 'SIGNED_IN' && session?.user) {
    console.log('🔍 Sign-in detected - letting AuthCallback handle user setup');
    // Don't call setUser here - let AuthCallback handle it with proper new/returning logic
  } else if (event === 'SIGNED_OUT') {
    console.log('👋 Sign-out detected - clearing user');
    useAuthStore.getState().clearUser();
  }
  // Ignore INITIAL_SESSION - let App.tsx handle that
});

// NEW: Expose sync function globally for debugging
if (typeof window !== 'undefined') {
  (window as any).syncOneSignalPlayerId = () => {
    const state = useAuthStore.getState();
    if (state.user?.id) {
      console.log('🔄 Manually syncing OneSignal Player ID...');
      state.syncOneSignalPlayerId();
    } else {
      console.log('❌ No user logged in');
    }
  };
}